import { useState, useEffect } from 'react';

export function useLocalStorage(key: any, initialValue: any) {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState(() => {
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key);
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      // If error also return initialValue
      console.error(error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value : any) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      // A more advanced implementation would handle the error case
      console.log(error);
    }
  };

  return [storedValue, setValue];
}

// Struggled with this function. It wouldn't update until I created setMapValue
//  and used setMapValue on the useEffect hook
export const useMapState = (key: string, defaultValue: Map<any, any>) => {
  const [value, setValue] = useState(() => {
    const map = localStorage.getItem(key);
    try {
      if (map !== null) return new Map(JSON.parse(map));
    } catch {
      console.error(
        "Caught an error when trying to set map to localstorage for key: ",
        key
      );
    }
    return defaultValue;
  });

  const setMapValue = (newValue: Map<any, any>) =>
    setValue(value => {
      return newValue;
    });

  useEffect(() => {
    window.localStorage.setItem(key, JSON.stringify(Array.from(value)));
  }, [key, setMapValue]);

  return [value, setMapValue] as const;
};
